






























































































































































/**
 * NOTE: When using the sync modifier, objects must always be passed with v-bind!
 *  ALl custom validations should evaluate to true to submit form. Invalid = false
 */
import { Component, Mixins, Prop } from "vue-property-decorator";
import {
  ChairOrderRecord,
  Order,
  OrderDetails,
  UmbrellaOrderRecord
} from "@/models/order/order";
import OrderService from "@/services/order_service";
import { GenericError } from "@/services/error_service";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderActions, OrderGetters } from "@/store/modules/order/types";
import {
  CustomChairOrder,
  CustomUmbrellaOrder
} from "@/models/configurator/custom_umbrella";
import { ProductType } from "@/models/products/product";
import { Mobile } from "@/mixins/mobile";
import OrderSummary from "@/components/checkout/OrderSummary.vue";
import ReviewFooter from "@/components/footer/ReviewFooter.vue";
import {
  CreateNetSuiteCustomerPayload,
  CreateNetSuiteQuotePayload,
  PaymentTerm
} from "@/models/netsuite/netsuite";
import { OrderMixin } from "@/mixins/orders/mixin";
import { GlobalGetters } from "@/store/modules/global/types";
import { PriceLevel } from "@/models/netsuite/netsuite";
import {
  NetSuiteEntity,
  NetSuiteEntityStatus
} from "@/models/netsuite/helpers";

@Component({
  components: {
    OrderSummary,
    ReviewFooter
  }
})
export default class OrderReview extends Mixins(Mobile, OrderMixin) {
  @(namespace(StoreModule.Order).Action(OrderActions.AddShipping))
  addShipping!: (value: OrderDetails["shipping_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddBilling))
  addBilling!: (value: OrderDetails["billing_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddPayment))
  addPayment!: (value: OrderDetails["payment_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddContact))
  addContact!: (value: OrderDetails["contact_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddOrderId))
  addOrderId!: (value: { id: string }) => Promise<void>;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  storeOrder!: Order;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetMultiplier))
  multiplier!: PriceLevel | null;
  protected loader: any = null;
  protected newlyCreatedCustomerId: number | null = null;
  protected orderService = new OrderService();
  protected error = {
    billing: false,
    shipping: false,
    payment: false,
    contact: false
  };
  @Prop() props!: any;

  created() {
    if (!this.props || !this.props.order) {
      this.$router.push("/resubmit?source=review");
    }
    window.scroll(0, 0);
  }

  protected get getPaymentTermText(): string {
    const methodId = this.props.order.payment_details.method;
    if (!methodId && methodId !== 0) return "N/A";
    const paymentTerms: PaymentTerm[] = this.props.paymentTerms || [];
    const term = paymentTerms.find(t => t.id === methodId);
    return term ? term.name : "N/A";
  }

  protected async checkout(): Promise<void> {
    this.loader = this.$loading.show(
      { opacity: 1 },
      {
        before: this.$createElement(
          "h1",
          "Submitting your quote... please do not refresh the page."
        )
      }
    );
    try {
      await this.saveDataToStore();
      const order = await this.parseItems();
      await this.createQuote(order);
    } catch (err) {
      console.error("[OrderReview] checkout() error:", err);
      GenericError.popup((err as any)?.message ?? "Something went wrong.");
    } finally {
      this.loader.hide();
    }
  }

  protected async saveDataToStore(): Promise<void> {
    await this.addShipping(this.props.order.shipping_details);
    await this.addBilling(this.props.order.billing_details);
    await this.addPayment(this.props.order.payment_details);
    await this.addContact(this.props.order.contact_details);
  }

  /**
   * The API expects only mfr_codes for fabric swatches. We save the entire fabric swatch object on the frontend because the fabric selector needs the extra data to do filtering
   */
  protected async parseItems(): Promise<Order> {
    const items = JSON.parse(JSON.stringify(this.storeOrder.items!));
    for (const item of items) {
      if (item.product_type === ProductType.Umbrella) {
        const parsedData = this.parseFabricFields(
          (item.item_details as CustomUmbrellaOrder)
            .canopy as CustomUmbrellaOrder["canopy"]
        );
        (item.item_details as CustomUmbrellaOrder).canopy = Object.assign(
          {},
          { ...(item.item_details as CustomUmbrellaOrder).canopy },
          { ...parsedData }
        );
      } else if (item.product_type === ProductType.Chair) {
        const parsedData = this.parseFabricFields(
          item.item_details as CustomChairOrder
        );
        (item.item_details as any) = Object.assign(
          {},
          { ...item.item_details },
          { ...parsedData } as ChairOrderRecord
        );
      }
    }
    const order = { ...this.storeOrder };
    order.items = [...items];
    return order;
  }

  /**
   * API is expecting strings for fabrics. parseFabricFields() convert swatch objects to strings if value is not already a string
   */
  protected parseFabricFields(
    fabric: CustomUmbrellaOrder["canopy"] | CustomChairOrder
  ): UmbrellaOrderRecord | ChairOrderRecord {
    const fabrics = [
      "mainFabric",
      "mainTrimFabric",
      "mainTrimFabricInner",
      "ventTopFabric",
      "ventMiddleFabric",
      "ventTrimFabric",
      "valanceFabric",
      "bindingFabric",
      "ribFabric",
      "undersideFabric"
    ];
    const parsed = {} as UmbrellaOrderRecord;
    for (const fabricKey of fabrics) {
      if (fabric[fabricKey] && fabric[fabricKey].mfr_code) {
        parsed[fabricKey] = fabric[fabricKey].mfr_code;
      } else if (
        fabric[fabricKey] &&
        Array.isArray(fabric[fabricKey]) &&
        fabric[fabricKey].length > 0
      ) {
        parsed[fabricKey] = fabric[fabricKey].map(swatch => {
          if (swatch.mfr_code) {
            return swatch.mfr_code;
          } else {
            return swatch;
          }
        });
      } else {
        parsed[fabricKey] = fabric[fabricKey];
      }
    }
    return parsed;
  }

  protected async createQuote(order: Order): Promise<void> {
    try {
      // ToDo: FNSI-101 - create NetSuite quote and then create unofficial quote.
      // commenting out the unofficial quote creation for now until we optimize the auth process and can associate quotes with users properly
      // DETERMINING IF WE CAN ELIMINATE THE "UNOFFICIAL" QUOTE CONCEPT ALTOGETHER AND JUST HAVE NETSUITE QUOTES
      this.newlyCreatedCustomerId = null;
      const res = await this.createNetSuiteQuote(order);
      // const quote = await this.orderService.createQuote(order);
      // await this.addOrderId({ id: quote._id!, creator: quote.creator_id });
      const query: Record<string, string> = { type: "quote" };
      if (this.newlyCreatedCustomerId) {
        query.newCustomerId = String(this.newlyCreatedCustomerId);
      }
      if (order.contact_details?.send_email) {
        query.emailSent = "1";
      }
      if (res?.estimate?.estimateId) {
        query.quoteId = String(res.estimate.estimateId);
      }
      if (res?.estimate?.tranId) {
        query.tranId = res.estimate.tranId;
      }
      await this.$router.push({ path: "/checkout/submitted", query });
    } catch (err) {
      GenericError.popup(err.message);
      this.$router.push("/cart");
    }
  }

  protected get isNewCustomer(): boolean {
    return !this.storeOrder.customer_netsuite_id;
  }

  protected async createNetSuiteCustomerIfNeeded(order: Order): Promise<Order> {
    if (!this.isNewCustomer) return order;
    const contact = order.contact_details;
    const shipping = order.shipping_details;
    const billing = order.billing_details;
    const email = contact.email;
    const shippingAddress = {
      addressee: shipping?.addressee ?? "",
      attention: shipping?.attention ?? "",
      addr1: shipping?.address?.address_1 ?? "",
      addr2: shipping?.address?.address_2 ?? "",
      city: shipping?.address?.city ?? "",
      state: shipping?.address?.state ?? "",
      zip: shipping?.address?.postal_code ?? "",
      country: shipping?.address?.country ?? "US"
    };
    const hasBilling =
      billing?.address?.address_1 ||
      billing?.address?.city ||
      billing?.address?.postal_code;
    const customerPriceLevelId = this.multiplier?.id ?? 1; // default price level ID of 1 is "Base Price"
    const payload: CreateNetSuiteCustomerPayload = {
      action: "createCustomer",
      recordType: NetSuiteEntity.Prospect,
      entitystatus: NetSuiteEntityStatus.Prospect_Proposal,
      category: "Retail",
      companyname: contact.company_name,
      email,
      phone: shipping?.phone ?? contact.phone,
      invoiceEmail: email,
      salesrep: this.user.netsuite_id,
      pricelevel: customerPriceLevelId,
      ...(order.payment_details?.method
        ? { terms: order.payment_details.method }
        : {}),
      shippingAddress: shippingAddress,
      ...(hasBilling
        ? {
            billingAddress: {
              addressee: billing?.addressee ?? "",
              attention: billing?.attention ?? "",
              addr1: billing?.address?.address_1 ?? "",
              addr2: billing?.address?.address_2 ?? "",
              addr3: billing?.address?.address_3 ?? "",
              city: billing?.address?.city ?? "",
              state: billing?.address?.state ?? "",
              zip: billing?.address?.postal_code ?? "",
              country: billing?.address?.country ?? "US"
            }
          }
        : {}),
      contact: {
        title: contact.title ?? null,
        firstname: contact.first_name,
        lastname: contact.last_name,
        email,
        phone: contact.phone ?? null
      }
    };
    const res = await this.netSuiteService.createNetSuiteCustomer(payload);
    console.log("NetSuite Customer created successfully:", res);
    const newCustomerNetSuiteId: number = res?.customerId;
    const newContactNetSuiteId: number = res?.contactId;
    if (!newCustomerNetSuiteId) {
      throw new Error(
        "Failed to retrieve new customer NetSuite ID from response."
      );
    }
    return {
      ...order,
      customer_netsuite_id: newCustomerNetSuiteId,
      customer_netsuite_price_level_id: customerPriceLevelId,
      customer_netsuite_contact_id: newContactNetSuiteId
    };
  }

  protected async createNetSuiteQuote(order: Order): Promise<any> {
    try {
      const orderWithCustomer = await this.createNetSuiteCustomerIfNeeded(
        order
      );
      if (
        orderWithCustomer.customer_netsuite_id !== order.customer_netsuite_id
      ) {
        this.newlyCreatedCustomerId =
          orderWithCustomer.customer_netsuite_id ?? null;
      }
      const payload: CreateNetSuiteQuotePayload = await this.prepOrderToCreateNetSuiteQuote(
        orderWithCustomer
      );
      console.log("Prepared NetSuite Quote Payload:", payload);
      const res = await this.netSuiteService.createNetSuiteQuote(payload);
      console.log("NetSuite Quote created successfully:", res);
      if (res?.status !== "success") {
        throw new Error(
          res?.status
            ? `NetSuite returned status: ${res.status}`
            : "NetSuite did not return a success status."
        );
      }
      return res;
    } catch (error) {
      console.error("Error creating NetSuite quote:", error);
      throw new Error(
        `Failed to create NetSuite quote. Try again. ${error.message}`
      );
    }
  }

  protected async createOrder(order: Order): Promise<void> {
    try {
      // ToDo: FNSI-101 - create NetSuite quote, request for that quote to be submitted, and then create the unofficial order through the Node API
      const res = await this.orderService.createOrder(order);
      this.$router.push("/checkout/submitted?type=order");
      this.loader.hide();
    } catch (err) {
      this.loader.hide();
      GenericError.popup(err.message);
    }
  }

  protected async convertToOrder(order: Order): Promise<void> {
    try {
      const res = await this.orderService.convertOrderToQuote(
        order._id!,
        order
      );
      this.$router.push("/checkout/submitted?type=order");
      this.loader.hide();
    } catch (err) {
      this.loader.hide();
      GenericError.popup(err.message);
    }
  }
}
